import { z } from 'zod';
import { jobCreateSchema, jobSchema } from './job.js';
import { idSchema, isoDateTime, pageQuerySchema, paginated } from './common.js';
/* ------------------------------- API intake ----------------------------- */
/**
 * POST /api/v1/intake/jobs — authenticated via integration API token.
 * Idempotent on (source, externalRef): replays return the existing job.
 */
export const intakeJobSchema = jobCreateSchema.safeExtend({
    source: z.literal('api').default('api'),
    correlationId: z.string().min(1).max(120),
    /** Resolve client by code instead of id when integrating. */
    clientCode: z.string().trim().min(2).max(32).optional(),
});
export const intakeAckSchema = z.object({
    status: z.enum(['accepted', 'duplicate']),
    jobId: idSchema.nullable(),
    externalRef: z.string(),
    correlationId: z.string(),
    /** Present when status === 'duplicate' and the replayed job id. */
    duplicateOf: idSchema.nullable().optional(),
});
/* ------------------------------ CSV intake ------------------------------ */
/** Canonical CSV column order for Shiptheory exports (docs/api.md §intake). */
export const CSV_REQUIRED_COLUMNS = [
    'external_ref',
    'client_code',
    'delivery_line1',
    'delivery_city',
    'delivery_postcode',
];
export const CSV_OPTIONAL_COLUMNS = [
    'correlation_id',
    'priority',
    'service_type',
    'collection_line1',
    'collection_line2',
    'collection_city',
    'collection_postcode',
    'collection_country',
    'collection_contact',
    'collection_phone',
    'delivery_line2',
    'delivery_country',
    'delivery_contact',
    'delivery_phone',
    'deadline_at',
    'window_start',
    'window_end',
    'description',
    'parcels',
    'weight_kg',
    'weight_unit',
    'handling_instructions',
    'internal_notes',
];
export const csvImportErrorSchema = z.object({
    field: z.string(),
    message: z.string(),
});
export const csvImportRowSchema = z.object({
    row: z.number().int().min(1),
    externalRef: z.string().nullable(),
    status: z.enum(['accepted', 'rejected']),
    jobId: idSchema.nullable(),
    errors: z.array(csvImportErrorSchema),
});
export const csvImportBatchSchema = z.object({
    id: idSchema,
    filename: z.string().max(255),
    source: z.literal('csv'),
    correlationId: z.string(),
    status: z.enum(['processing', 'completed', 'failed']),
    totalRows: z.number().int().min(0),
    accepted: z.number().int().min(0),
    rejected: z.number().int().min(0),
    uploadedBy: idSchema,
    createdAt: isoDateTime,
    completedAt: isoDateTime.nullish(),
});
export const csvImportBatchDetailSchema = csvImportBatchSchema.extend({
    rows: z.array(csvImportRowSchema),
});
export const csvImportBatchListSchema = paginated(csvImportBatchSchema);
export const csvImportBatchListQuerySchema = pageQuerySchema;
/* ------------------------------- job export ----------------------------- */
export const exportQuerySchema = z.object({
    format: z.enum(['csv']).default('csv'),
    /** Reuses the exact filters as the list endpoint. */
    status: z.union([z.string(), z.array(z.string())]).optional(),
    search: z.string().max(200).optional(),
    clientId: idSchema.optional(),
    driverId: idSchema.optional(),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
});
export const jobExportRowSchema = jobSchema.pick({
    id: true,
    externalRef: true,
    status: true,
    priority: true,
    serviceType: true,
    weightKg: true,
    deadlineAt: true,
});
//# sourceMappingURL=intake.js.map